/* eslint-disable @typescript-eslint/no-explicit-any */
import axios from 'axios';
import { getToken } from './api';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

apiClient.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ==================== ORDER API ====================

export interface OrderItem {
  id: number;
  meal_id: number | null;
  meal_name: string;
  meal_description: string | null;
  quantity: number;
  unit_price: number;
  total_price: number;
  portion_size_g: number;
  portion_multiplier: number;
  calories: number | null;
  protein_g: number | null;
  carbohydrates_g: number | null;
  fat_g: number | null;
  customizations: any[];
  special_instructions: string | null;
}

export interface Order {
  id: number;
  order_number: string;
  user_id: number;
  status: string;
  payment_status: string;
  subtotal: number;
  discount_amount: number;
  delivery_fee: number;
  tax_amount: number;
  total_amount: number;
  promo_code: string | null;
  delivery_address: string;
  delivery_slot: string;
  scheduled_date: string;
  estimated_delivery_time: string | null;
  actual_delivery_time: string | null;
  special_instructions: string | null;
  delivery_person_name: string | null;
  delivery_person_phone: string | null;
  delivery_otp: string | null;
  tracking_url: string | null;
  items: OrderItem[];
  created_at: string;
  updated_at: string;
}

export interface OrderListResponse {
  items: Order[];
  total: number;
  page: number;
  page_size: number;
}

export interface OrderItemCreate {
  meal_id?: number;
  meal_name?: string;
  quantity?: number;
  portion_multiplier?: number;
  special_instructions?: string;
  unit_price?: number;
  calories?: number;
  protein_g?: number;
  carbs_g?: number;
  fat_g?: number;
}

export interface OrderCreate {
  items: OrderItemCreate[];
  delivery_slot: '7-9am' | '12-2pm' | '6-8pm';
  scheduled_date: string;
  special_instructions?: string;
  source?: 'menu' | 'custom';
}

export const orderApi = {
  createOrder: async (data: OrderCreate): Promise<Order> => {
    const response = await apiClient.post('/orders/', data);
    return response.data;
  },

  getOrders: async (params?: {
    page?: number;
    page_size?: number;
    status?: string;
  }): Promise<OrderListResponse> => {
    const response = await apiClient.get('/orders/', { params });
    return response.data;
  },

  getOrderById: async (id: number): Promise<Order> => {
    const response = await apiClient.get(`/orders/${id}`);
    return response.data;
  },

  cancelOrder: async (id: number): Promise<any> => {
    const response = await apiClient.post(`/orders/${id}/cancel`);
    return response.data;
  },

  getDeliveryTracking: async (id: number): Promise<any> => {
    const response = await apiClient.get(`/orders/${id}/tracking`);
    return response.data;
  },
};

export default orderApi;
